/**
 * Error page component.
 *
 * When Kratos redirects here with ?id=<error_container_id>, fetches the
 * full error details so the user sees the actual reason (e.g. "forbidden
 * return URL") instead of a generic message.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { kratos } from '../lib/kratos';

interface KratosError {
  id?: string;
  code?: number;
  status?: string;
  reason?: string;
  message?: string;
}

export default function Error() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const errorId = params.get('id');
  const errorMessage = params.get('error');

  const [kratosError, setKratosError] = useState<KratosError | null>(null);
  const [loading, setLoading] = useState(!!errorId);

  useEffect(() => {
    if (!errorId) return;

    kratos.getFlowError({ id: errorId })
      .then(({ data }) => {
        const inner = data.error as KratosError | undefined;
        if (inner) setKratosError(inner);
      })
      .catch(() => {
        // Error container not found or expired — show what we have
      })
      .finally(() => setLoading(false));
  }, [errorId]);

  const displayMessage = kratosError?.message || errorMessage;
  const isNotFound = !displayMessage && !errorId;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-content-primary mb-2">
          {isNotFound ? 'Page Not Found' : loading ? 'Loading...' : 'Error'}
        </h1>

        <p className="text-content-secondary mb-2">
          {loading
            ? 'Fetching error details...'
            : displayMessage || "The page you're looking for doesn't exist or has been moved."}
        </p>

        {kratosError?.reason && kratosError.reason !== kratosError.message && (
          <p className="text-sm text-content-tertiary mb-4">
            {kratosError.reason}
          </p>
        )}

        {(kratosError?.id || errorId) && (
          <p className="text-xs font-mono text-content-tertiary bg-surface-800 rounded px-3 py-1.5 inline-block mb-4">
            {kratosError?.id || errorId}
            {kratosError?.code ? ` (${kratosError.code})` : ''}
          </p>
        )}

        <div className="flex justify-center space-x-4">
          <Link to="/login" className="btn-primary">
            Go to Login
          </Link>
          <button
            onClick={() => window.history.back()}
            className="btn-secondary"
          >
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}
